import React, { useMemo, useState } from "react";
import { toast } from "sonner";
import { Copy, CheckCircle, ArrowSquareOut, Lightning, Info } from "@phosphor-icons/react";
import {
  Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription,
} from "@/components/ui/sheet";
import { Button } from "@/components/ui/button";
import { STATUS_CONFIG } from "@/components/Layout";
import { buildNapBlock, buildDirectoryContent } from "@/lib/content";

export default function BulkAssist({ open, onOpenChange, business, directories, citations, onPatch }) {
  const [copiedMaster, setCopiedMaster] = useState(false);
  const [opened, setOpened] = useState({});

  const napBlock = useMemo(() => buildNapBlock(business || {}), [business]);

  const remaining = directories.filter((d) => {
    const c = citations[d.id] || { status: "not_started" };
    return c.status !== "live";
  });
  const sorted = [...remaining].sort((a, b) => (a.priority || 9) - (b.priority || 9));

  const copyMaster = () => {
    navigator.clipboard.writeText(napBlock);
    setCopiedMaster(true);
    toast.success("Master business info copied");
    setTimeout(() => setCopiedMaster(false), 1500);
  };

  const openAndCopy = (d) => {
    navigator.clipboard.writeText(buildDirectoryContent(business, d));
    window.open(d.submit_url, "_blank", "noopener,noreferrer");
    setOpened((o) => ({ ...o, [d.id]: true }));
    const c = citations[d.id] || { status: "not_started" };
    if (c.status === "not_started") onPatch(d.id, { status: "in_progress" });
    toast.success(`${d.name}: content copied — paste it into the form`);
  };

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent className="w-full sm:max-w-xl overflow-y-auto" data-testid="bulk-assist-sheet">
        <SheetHeader className="text-left">
          <p className="text-xs font-bold uppercase tracking-[0.12em] text-[#0055FF]">Bulk Assist</p>
          <SheetTitle className="font-display text-2xl font-bold tracking-tight">Prepare all submissions</SheetTitle>
          <SheetDescription>
            Copy your master info once, then for each directory click "Open & copy" — we open the submission page and copy that site's listing so you just paste and submit.
          </SheetDescription>
        </SheetHeader>

        <div className="mt-5 rounded-md bg-blue-50 border border-blue-100 p-3 flex gap-2.5 text-sm text-blue-800">
          <Info size={18} weight="fill" className="shrink-0 mt-0.5 text-[#0055FF]" />
          <p>Directories don't allow true one-click auto-posting, but this cuts each listing down to a paste. Opened sites are auto-marked <strong>In progress</strong>.</p>
        </div>

        <div className="mt-5 rounded-md bg-slate-900 p-4">
          <div className="flex items-center justify-between mb-3">
            <p className="text-xs font-bold uppercase tracking-[0.1em] text-slate-400">Master business info</p>
            <Button size="sm" onClick={copyMaster} data-testid="bulk-copy-master"
              className="h-8 bg-[#0055FF] hover:bg-[#0044CC] text-white">
              {copiedMaster ? <><CheckCircle weight="fill" size={15} className="mr-1" /> Copied</> : <><Copy size={15} className="mr-1" /> Copy info</>}
            </Button>
          </div>
          <pre className="font-mono text-xs text-slate-100 whitespace-pre-wrap leading-relaxed">{napBlock}</pre>
        </div>

        <div className="mt-6">
          <div className="flex items-center gap-2 mb-3">
            <Lightning weight="fill" size={16} className="text-[#0055FF]" />
            <p className="text-xs font-bold uppercase tracking-[0.1em] text-slate-500">
              {sorted.length} directories to submit
            </p>
          </div>

          {sorted.length === 0 ? (
            <div className="py-10 text-center text-slate-500 text-sm">All directories are already live. 🎉</div>
          ) : (
            <div className="rounded-xl border border-slate-200 overflow-hidden">
              {sorted.map((d) => {
                const c = citations[d.id] || { status: "not_started" };
                const cfg = STATUS_CONFIG[c.status] || STATUS_CONFIG.not_started;
                return (
                  <div key={d.id} data-testid={`bulk-row-${d.id}`}
                    className="flex items-center justify-between gap-3 px-4 py-3 border-b border-slate-100 last:border-0 hover:bg-slate-50/60 transition-colors">
                    <div className="min-w-0">
                      <div className="flex items-center gap-2">
                        <span className="font-semibold text-slate-900 truncate">{d.name}</span>
                        {d.priority === 1 && <span className="text-[10px] font-bold uppercase tracking-wide text-[#0055FF] bg-blue-50 border border-blue-100 rounded px-1.5 py-0.5">Priority</span>}
                      </div>
                      <span className={`inline-flex items-center gap-1.5 mt-1 rounded-full border px-2 py-0.5 text-[11px] font-semibold ${cfg.badge}`}>
                        <span className={`h-1.5 w-1.5 rounded-full ${cfg.dot}`} />{cfg.label}
                      </span>
                    </div>
                    <Button size="sm" onClick={() => openAndCopy(d)} data-testid={`bulk-open-${d.id}`}
                      variant={opened[d.id] ? "secondary" : "default"}
                      className={opened[d.id]
                        ? "bg-white border border-slate-200 text-slate-700 hover:bg-slate-50 shrink-0"
                        : "bg-[#0055FF] hover:bg-[#0044CC] text-white shrink-0"}>
                      {opened[d.id] ? <><CheckCircle weight="fill" size={15} className="mr-1.5 text-emerald-500" /> Reopen</> : <><ArrowSquareOut size={15} className="mr-1.5" /> Open & copy</>}
                    </Button>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </SheetContent>
    </Sheet>
  );
}
